import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import { UserTypeChips } from './userTypes';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const Field = ({ label, value, icon }) => (
    <div>
        <div className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400 tw-font-semibold tw-mb-0.5">
            {label}
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-800 tw-font-medium tw-break-all">
            {icon ? <Icon icon={icon} className="tw-text-rose-500 tw-text-[11px] tw-flex-shrink-0" /> : null}
            <span>{value || '—'}</span>
        </div>
    </div>
);

const ViewAdminUserModal = ({ open, onClose, admin }) => {
    if (!admin) return null;

    const raw = admin.raw || {};
    const isActive = admin.status === 'Active';
    const initial = (admin.adminName || admin.email || 'A').charAt(0).toUpperCase();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, maxWidth: 520 }
            }}
        >
            {/* Header — a div, not DialogTitle's <h2>, which inherits the theme's large heading size */}
            <DialogTitle component="div" sx={{ p: 0 }}>
                <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
                }}>
                    <div className="tw-text-sm tw-font-semibold tw-text-gray-800">User Details</div>
                    <IconButton aria-label="close" onClick={onClose} size="small" sx={{ color: 'text.secondary', p: 0.5 }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, '&.MuiDialogContent-root': { paddingTop: 0 } }}>
                {/* Identity strip */}
                <div className="tw-px-4 tw-py-2.5 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-bg-rose-50/30">
                    <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-rose-100 tw-flex tw-items-center tw-justify-center tw-text-rose-600 tw-font-bold tw-text-sm tw-flex-shrink-0">
                        {initial}
                    </div>
                    <div className="tw-min-w-0 tw-flex-1">
                        <div className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-truncate">
                            {admin.adminName || '—'}
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-0.5 tw-flex-wrap">
                            <span className="tw-text-[10px] tw-text-gray-400 tw-font-mono">#{admin.id}</span>
                            <UserTypeChips type={admin.userType} types={admin.userTypes} />
                            <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-border ${
                                isActive
                                    ? 'tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-100'
                                    : 'tw-bg-gray-50 tw-text-gray-500 tw-border-gray-100'
                            }`}>
                                <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${isActive ? 'tw-bg-emerald-500' : 'tw-bg-gray-400'}`}></span>
                                {admin.status}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Field grid */}
                <div className="tw-px-4 tw-py-3 tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3">
                    <Field icon="solar:letter-bold" label="Email" value={raw?.email || admin?.email} />
                    <Field icon="solar:phone-bold" label="Phone" value={raw?.phone} />
                    <Field icon="solar:user-bold" label="First Name" value={raw?.first_name} />
                    <Field icon="solar:user-bold" label="Last Name" value={raw?.last_name} />
                    <Field icon="solar:calendar-add-bold" label="Created On" value={formatDate(raw?.created_at)} />
                    <Field icon="solar:refresh-bold" label="Last Updated" value={formatDate(raw?.updated_at)} />
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                <button
                    type="button"
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    onClick={onClose}
                >
                    <Icon icon="solar:close-circle-outline" className="tw-text-sm" />
                    <span>Close</span>
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewAdminUserModal;
